import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { CountryFilterPair, Option } from '@utils/pageConfig/Types';
import ProductGroupModal from '../product-group-modal/ProductGroupModal';

import * as styles from './ContextBar.module.scss';
import { sortOptionsFromApi } from '@utils/FilterSettings';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { BASE_RETAIL_PATH, RETAIL_ALLOWED_PATH } from '@utils/UrlUtils';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { generateProductGroupsByPage } from '@utils/FilterUtils';
import { getSubscriptionDetailsByProductType } from '@utils/AppSubscriptionUtils';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { RelativePeriod } from '../comparison-period-selector/Types';

interface ContextBarProps {
  setSelectedProductGroup: (pg: Option) => void;
  setSelectedCountries: (country: Option[]) => void;
  isShown: boolean;
  setShowPgModal: Dispatch<SetStateAction<boolean>>;
  initialFilters: MainFilters[];
  initialFiltersProducts: MainFilters[];
  loadingFeatureValue: boolean;
  productType: ProductType;
  productDataType: ProductDataType;
  periodDispatchActions?: {
    updatePeriod: (period: BaseTimePeriod) => void;
    updatePeriodicity: (periodicity: Periodicity) => void;
    updateComparisonPeriod: (comparisonPeriod: RelativePeriod | BaseTimePeriod) => void;
  };
  isMultiCountry?: boolean;
  setApplyContext?: (load: boolean) => void;
}

const ContextBar: React.FC<ContextBarProps> = ({
  setSelectedProductGroup,
  setSelectedCountries,
  isShown,
  setShowPgModal,
  initialFilters,
  initialFiltersProducts,
  loadingFeatureValue,
  productType,
  productDataType,
  periodDispatchActions,
  isMultiCountry,
  setApplyContext,
}) => {
  const [isModalShown, setIsModalShown] = useState<boolean>(isShown);
  const [rawProductGroupResponse, setRawProductGroupResponse] = useState<CountryFilterPair[]>([]);
  const [rawProductGroupResponseProduct, setRawProductGroupResponseProduct] = useState<CountryFilterPair[]>([]);
  const [productGroups, setProductGroups] = useState<any>([]);
  const pagesConfig = usePageConfig();

  const navigate = useNavigate();
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;

  const applyContext = (selectedProductGroup: Option, selectedCountry: Option[]) => {
    setSelectedProductGroup(selectedProductGroup);
    setSelectedCountries(selectedCountry);
    setApplyContext?.(true);
    hideModal();
  };

  const hideModal = () => {
    setIsModalShown(false);
    setShowPgModal(false);
  };

  const hideModalAndResetState = () => {
    hideModal();
    navigate(`/${BASE_RETAIL_PATH}/${RETAIL_ALLOWED_PATH.EXPLORE}`);
  };

  useEffect(() => {
    generateProductGroupsByPage(pagesConfig).then((pgResponse) => {
      if (pgResponse) {
        setRawProductGroupResponse(pgResponse.dataForPerformance);
        setRawProductGroupResponseProduct(pgResponse.dataForProduct);
        setProductGroups(
          sortOptionsFromApi(
            ProductDataType.PerformanceAnalyzer === productDataType
              ? pgResponse.uniquePerformanceOptions
              : pgResponse.uniqueProductOptions,
          ),
        );
      }
    });
  }, [
    pagesConfig.clientCountries,
    pagesConfig.hitlistsClientCountries,
    pagesConfig.sellerInsightsClientCountries,
    pagesConfig.marketPremiumClientCountries,
  ]);

  return (
    <div className={styles.ContextBar}>
      <ProductGroupModal
        rawProductGroupResponse={rawProductGroupResponse}
        rawProductGroupResponseProduct={rawProductGroupResponseProduct}
        productGroups={productGroups}
        countries={currentClientCountries}
        onApply={applyContext}
        isOpen={isModalShown}
        onClose={hideModalAndResetState}
        setProductGroup={(pg: Option) => setSelectedProductGroup(pg)}
        initialFilters={initialFilters}
        initialFiltersProducts={initialFiltersProducts}
        isDisabled={loadingFeatureValue}
        productType={productType}
        periodDispatchActions={periodDispatchActions}
        isMultiCountry={isMultiCountry}
      />
    </div>
  );
};
export default ContextBar;
